import type { IndicatorRegistryEntry } from "lightweight-charts-indicators"
import { useEffect, useMemo, useRef, useState } from "react"
import { changedValues, type FormInput, InputGroups } from "../../ui/InputField.tsx"
import { Modal } from "../../ui/Modal.tsx"
import type { IndicatorStyle } from "../workspace/indicatorStyleSchema.ts"
import type { IndicatorInstance } from "../workspace/workspaceStore.ts"
import { pruneStyle, styleRows } from "./indicatorStyle.ts"
import { IndicatorStyleTab } from "./IndicatorStyleTab.tsx"

type Tab = "inputs" | "style"

/**
 * TradingView's indicator settings: the Inputs and Style tabs. Style changes
 * show on the chart as you make them; Cancel (or closing) puts back the saved
 * style, Ok keeps them. Only what differs from the defaults is stored.
 */
export function IndicatorSettingsDialog({ entry, instance, onApply, onClose, onPreview }: {
  readonly entry: IndicatorRegistryEntry
  readonly instance: IndicatorInstance
  readonly onApply: (patch: { inputs: Record<string, unknown>; style: IndicatorStyle | undefined }) => void
  /** Draws an unsaved style (null: the saved one again). */
  readonly onPreview: (style: IndicatorStyle | undefined | null) => void
  readonly onClose: () => void
}) {
  const defaults = entry.defaultInputs as Record<string, unknown>
  const config = entry.inputConfig as ReadonlyArray<FormInput>
  const rows = useMemo(() => styleRows(entry), [entry])
  const [tab, setTab] = useState<Tab>("inputs")
  const [values, setValues] = useState<Record<string, unknown>>({ ...defaults, ...instance.inputs })
  const [style, setStyle] = useState<IndicatorStyle>(instance.style ?? {})

  // One preview per animation frame (the opacity slider fires on every pixel).
  const frame = useRef(0)
  const changeStyle = (next: IndicatorStyle) => {
    setStyle(next)
    cancelAnimationFrame(frame.current)
    frame.current = requestAnimationFrame(() => onPreview(next))
  }
  useEffect(() => () => cancelAnimationFrame(frame.current), [])

  const applied = useRef(false)
  // X, the backdrop, Escape, and Cancel all close without keeping the preview.
  const close = () => {
    cancelAnimationFrame(frame.current)
    if (!applied.current) onPreview(null)
    onClose()
  }

  return (
    <Modal title={entry.name} onClose={close}>
      <nav className="dialog-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === "inputs"} onClick={() => setTab("inputs")}>Inputs</button>
        <button type="button" role="tab" aria-selected={tab === "style"} onClick={() => setTab("style")}>Style</button>
      </nav>
      <form
        noValidate
        className="settings-form"
        onSubmit={(event) => {
          event.preventDefault()
          applied.current = true
          cancelAnimationFrame(frame.current)
          onApply({ inputs: changedValues(config, values, defaults), style: pruneStyle(entry, style) })
          onClose()
        }}
      >
        {tab === "inputs"
          ? (
            <>
              <InputGroups inputs={config} values={values} onChange={(id, value) => setValues((v) => ({ ...v, [id]: value }))} />
              <label className="style-check">
                <input
                  type="checkbox"
                  checked={style.inputsInStatusLine !== false}
                  onChange={(event) => changeStyle({ ...style, inputsInStatusLine: event.target.checked })}
                />
                Inputs in status line
              </label>
            </>
          )
          : <IndicatorStyleTab plots={rows.plots} levels={rows.levels} fills={rows.fills} style={style} onChange={changeStyle} />}
        <footer className="settings-actions">
          <button
            type="button"
            className="link"
            onClick={() => (tab === "inputs" ? setValues({ ...defaults }) : changeStyle({}))}
          >
            Reset to defaults
          </button>
          <span className="toolbar-spacer" />
          <button type="button" onClick={close}>Cancel</button>
          <button type="submit" className="primary">Ok</button>
        </footer>
      </form>
    </Modal>
  )
}
